import {
    DialogContent,
    DialogAwareContentContainer,
    DialogAwareButtonContainer,
    Button,
    AccountAddress,
    Icon,
} from "@/components";
import { useReallocatePoints } from "@/hooks";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

export interface ReallocatePointsDialogProps {
    onClose?: () => void;
}

// Define the form schema with validation
const schema = yup.object().shape({
    newAddress: yup
        .string()
        .required("Address is required")
        .matches(/^0x[a-fA-F0-9]{40}$/, "Invalid Ethereum address format")
        .test("not-current-address", "Cannot transfer to the same address", () => {
            // This will be checked in the component
            return true;
        }),
});

type FormValues = {
    newAddress: string;
};

export function ReallocatePointsDialog(_: ReallocatePointsDialogProps) {
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const newAddress = watch("newAddress");

    // Update the reallocate points hook with the current form value
    const { executeReallocatePoints: submitForm, isLoading: isSubmitting } = useReallocatePoints({
        newAddress: newAddress || "",
    });

    const onSubmit = async () => {
        await submitForm();
    };

    return (
        <DialogContent>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogAwareContentContainer isDialog>
                    <div className="space-y-4">
                        <div className="border-secondary bg-secondary/20 py-3 px-3 rounded">
                            <span className="text-secondary inline-flex gap-1 items-center">
                                <Icon name="Warning" className="w-4 h-4 text-secondary" />
                                Reassigning your referral code is a one time operation. You will lose the referral
                                points you have earned so far.
                            </span>
                        </div>

                        <div className="flex space-x-1 justify-between items-center">
                            <span className="text-[#7F7887]">Current address</span>
                            <AccountAddress truncate className="text-white" />
                        </div>

                        <div className="flex flex-col space-y-1">
                            <span className="text-[#7F7887]">Transfer to wallet address</span>
                            <input
                                type="text"
                                {...register("newAddress")}
                                className={`border ${
                                    errors.newAddress ? "border-red-500" : "border-[#1A1A1A]"
                                } rounded-md p-2 font-mono `}
                                placeholder="0x..."
                            />
                            {errors.newAddress && <span className="text-red-500">{errors.newAddress.message}</span>}
                        </div>
                    </div>
                </DialogAwareContentContainer>

                <DialogAwareButtonContainer isDialog>
                    <Button
                        type="submit"
                        disabled={isSubmitting || !newAddress || !!errors.newAddress}
                        isLoading={isSubmitting}
                        fullWidth
                        className="bg-green-700 hover:bg-green-600"
                    >
                        Transfer
                    </Button>
                </DialogAwareButtonContainer>
            </form>
        </DialogContent>
    );
}
